import { useMemo, useRef, useState } from 'react';
import type { Proposal, Tag, User } from '@hestia/core';
import { api } from '../../api.js';
import { Sheet } from '../../components/Sheet.js';
import { Avatar, Icon, icons, tintVar, onTintVar } from '../../ui.js';
import { longDate, relativeDay } from '../../lang.js';

/** What was heard, before any of it counts.
 *
 *  One card per thing understood, swiped through rather than stacked, because a
 *  list of five half-read cards invites approving all of them without reading
 *  any. One at a time is slower and that is the point: each one gets looked at.
 *
 *  Nothing here has happened yet. Everything is applied at the end, through the
 *  ordinary create and update paths, so a task made by talking is identical to
 *  one made by typing. */

/** The ones there is something to do about. `unclear` is shown, never acted on. */
type Actionable = Exclude<Proposal, { kind: 'unclear' }>;

type Props = {
  transcript: string;
  proposals: Proposal[];
  today: string;
  users: User[];
  tags: Tag[];
  onClose: () => void;
  onApplied: () => void;
};

export function Review({ transcript, proposals, today, users, tags, onClose, onApplied }: Props) {
  const usable = useMemo(
    () => proposals.filter((p): p is Actionable => p.kind !== 'unclear'), [proposals]);
  const skipped = useMemo(() => proposals.filter((p) => p.kind === 'unclear'), [proposals]);

  const [keep, setKeep] = useState<boolean[]>(() => usable.map(() => true));
  const [at, setAt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const track = useRef<HTMLDivElement>(null);

  const kept = keep.filter(Boolean).length;

  function scrollTo(i: number) {
    const el = track.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  }

  async function apply() {
    if (busy || !kept) return;
    setBusy(true);
    setError(null);
    try {
      for (const [i, p] of usable.entries()) {
        if (!keep[i]) continue;
        if (p.kind === 'create') {
          const d = p.draft;
          const who = d.assigneeHint
            ? users.find((u) => u.username === d.assigneeHint
                || u.displayName.toLowerCase() === d.assigneeHint)
            : undefined;
          await api.create({
            title: d.title,
            startDate: d.startDate,
            startTime: d.startTime,
            durationMin: d.durationMin,
            rrule: d.rrule,
            assigneeId: who?.id ?? null,
            tags: d.tagHints,
          });
        } else if (p.kind === 'move') {
          await api.move({ task: { id: p.taskId, title: p.title }, date: p.from,
                           originalDate: p.from, done: false }, p.to);
        } else if (p.kind === 'done') {
          await api.complete({ task: { id: p.taskId, title: p.title }, date: p.date,
                               originalDate: p.date, done: false }, true);
        }
      }
      onApplied();
      onClose();
    } catch {
      setError('Could not save those. Try again in a moment.');
      setBusy(false);
    }
  }

  if (!usable.length) {
    return (
      <Sheet onClose={onClose}>
        <div className="cmp-head"><button className="cmp-cancel" onClick={onClose}>Close</button></div>
        <div className="sheet-body cmp-body">
          <div className="dsp" style={{ fontSize: 23 }}>Nothing I could act on</div>
          <Heard transcript={transcript} />
          <div className="dp-echo">Try naming the thing and the day — “bins out on Wednesday”.</div>
        </div>
        <div className="cmp-foot" />
      </Sheet>
    );
  }

  return (
    <Sheet onClose={onClose}>
      <div className="cmp-head">
        <button className="cmp-cancel" onClick={onClose}>Cancel</button>
        <div style={{ flexGrow: 1 }} />
        {usable.length > 1 && (
          <div className="vr-pips" aria-hidden>
            {usable.map((_, i) => <span key={i} className={i === at ? 'on' : ''} />)}
          </div>
        )}
      </div>

      <div className="sheet-body cmp-body">
        <div className="dsp" style={{ fontSize: 23 }}>Here’s what I heard</div>
        <Heard transcript={transcript} />

        <div className="vr-track" ref={track}
             onScroll={(e) => {
               const el = e.currentTarget;
               setAt(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)));
             }}>
          {usable.map((p, i) => (
            <div className="vr-slide" key={i}>
              <Card p={p} today={today} users={users} tags={tags}
                    on={keep[i]!} onToggle={() => setKeep((k) => k.map((v, j) => j === i ? !v : v))} />
            </div>
          ))}
        </div>

        {usable.length > 1 && (
          <div className="vr-nav">
            <button className="vr-arrow" aria-label="Previous" disabled={at === 0}
                    onClick={() => scrollTo(at - 1)}><Icon path={icons.chevronLeft} size={17} /></button>
            <span className="dp-echo">Swipe to see the rest</span>
            <button className="vr-arrow" aria-label="Next" disabled={at >= usable.length - 1}
                    onClick={() => scrollTo(at + 1)}><Icon path={icons.chevronRight} size={17} /></button>
          </div>
        )}

        {skipped.length > 0 && (
          <div className="notice" style={{ alignItems: 'flex-start' }}>
            <Icon path={icons.info} size={16} />
            <span>Left out: {skipped.map((s) => `“${s.text}”`).join(', ')}</span>
          </div>
        )}

        {error && <div style={{ fontSize: 13.5, color: 'var(--amber-on)' }}>{error}</div>}
      </div>

      <div className="cmp-foot">
        <button className="pill pill-accent" style={{ height: 54, width: '100%', fontSize: 16 }}
                disabled={!kept || busy} onClick={apply}>
          {busy ? 'Saving…' : kept === usable.length && kept > 1 ? 'Add all of these'
            : kept === 1 ? 'Add this one' : 'Add the ticked ones'}
        </button>
      </div>
    </Sheet>
  );
}

const Heard = ({ transcript }: { transcript: string }) => (
  <div className="notice" style={{ alignItems: 'flex-start', lineHeight: 1.45 }}>
    <Icon path={icons.sparkle} size={16} />
    <span>“{transcript}”</span>
  </div>
);

function Card({ p, today, users, tags, on, onToggle }: {
  p: Actionable; today: string; users: User[]; tags: Tag[]; on: boolean; onToggle: () => void;
}) {
  const label = p.kind === 'create' ? 'New task' : p.kind === 'move' ? 'Move' : 'Finished';
  return (
    <div className={`vr-card${on ? '' : ' off'}`}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 13 }}>
        <div style={{ flexGrow: 1, minWidth: 0 }}>
          <div className="sec">{label}</div>
          <div className="row-title" style={{ marginTop: 6 }}>
            {p.kind === 'create' ? p.draft.title : p.title}
          </div>
          <div className="row-meta">{meta(p, today, users, tags)}</div>
        </div>
        <button className={`vr-tick${on ? ' on' : ''}`} aria-pressed={on} onClick={onToggle}
                aria-label={on ? 'Do not add this' : 'Add this'}>
          <Icon path={icons.check} size={12} w={3} />
        </button>
      </div>
    </div>
  );
}

function meta(p: Actionable, today: string, users: User[], tags: Tag[]) {
  if (p.kind === 'move') {
    return (
      <>
        <span className="chip" style={{ textDecoration: 'line-through', color: 'var(--ink-4)' }}>
          {relativeDay(p.from, today)}
        </span>
        <span className="chip" style={{ color: 'var(--accent-on)', fontWeight: 600 }}>
          <Icon path={icons.arrowRight} size={12} />{relativeDay(p.to, today)}
        </span>
      </>
    );
  }
  if (p.kind === 'done') {
    return <span className="chip">{longDate(p.date)}</span>;
  }
  const d = p.draft;
  const who = d.assigneeHint
    ? users.find((u) => u.username === d.assigneeHint
        || u.displayName.toLowerCase() === d.assigneeHint)
    : undefined;
  return (
    <>
      <span className="chip"><Icon path={icons.calendar} size={13} />{relativeDay(d.startDate, today)}</span>
      {d.startTime && <span className="chip"><Icon path={icons.clock} size={13} />{d.startTime}</span>}
      {who && <span className="chip" style={{ gap: 6 }}><Avatar user={who} size={20} />{who.displayName}</span>}
      {d.tagHints.map((h: string) => {
        const t = tags.find((x) => x.slug === h);
        return (
          <span key={h} className="tag"
                style={t ? { background: tintVar(t.hue), color: onTintVar(t.hue) }
                         : { background: 'var(--soft)', color: 'var(--ink-2)' }}>
            {t ? t.name : h}
          </span>
        );
      })}
    </>
  );
}
